import React from "react";

const Policy = () => {
    // Styles for a clean, readable legal page
    const styles = {
        container: {
            maxWidth: "900px",
            margin: "0 auto",
            padding: "40px 20px",
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            lineHeight: "1.7",
            color: "#2c3e50",
        },
        header: {
            textAlign: "center",
            marginBottom: "40px",
            borderBottom: "2px solid #eee",
            paddingBottom: "20px",
        },
        h1: {
            fontSize: "2.5rem",
            marginBottom: "10px",
            color: "#1a1a1a",
        },
        h3: {
            fontSize: "1.4rem",
            marginTop: "35px",
            marginBottom: "15px",
            color: "#2c3e50",
            borderLeft: "4px solid #007bff", // Nice accent color
            paddingLeft: "15px",
        },
        section: {
            marginBottom: "20px",
        },
        list: {
            paddingLeft: "20px",
            marginBottom: "15px",
        },
        listItem: {
            marginBottom: "10px",
        },
        warningBox: {
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeeba",
            padding: "15px",
            borderRadius: "5px",
            margin: "20px 0",
            color: "#856404",
        },
        contactBox: {
            backgroundColor: "#e2e3e5",
            padding: "20px",
            borderRadius: "5px",
            marginTop: "20px",
            textAlign: "center",
            fontWeight: "bold",
        },
        link: {
            color: "#007bff",
            textDecoration: "none",
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.h1}>Terms and Conditions</h1>
                <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            <div style={styles.section}>
                <p>
                    Welcome to <strong>Kanchans Choice</strong>. By accessing our website and purchasing our products, you agree to be bound by the following Terms and Conditions ("Terms"). Please read them carefully before using our services.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>1. AI Representation & Product Imagery</h3>
                <div style={styles.warningBox}>
                    <strong>Transparency Notice:</strong> Some imagery on this website, including models showcasing clothing or accessories, may be generated using Artificial Intelligence (AI).
                </div>
                <p>
                    We utilize advanced technology to visualize our products. While we ensure the product design, fabric, and color are represented as closely as possible to reality, AI-generated images are artistic renderings.
                </p>
                <p>
                    <strong>What this means for you:</strong> The fit, drape, and texture on a physical human body may vary slightly from the AI model visualization. We recommend referring to our size charts and detailed product descriptions for the most accurate information.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>2. Returns and Refunds</h3>
                <p>
                    At Kanchans Choice, customer satisfaction is our priority. Unlike many online retailers, we are happy to discuss returns if you are not satisfied with your purchase.
                </p>
                <p>
                    <strong>How to initiate a return:</strong>
                </p>
                <div style={styles.contactBox}>
                    To request a return, please contact Customer Support immediately at:<br />
                    <span style={{ fontSize: "1.2em", display: "block", marginTop: "10px" }}>
            📞 571-699-6505
          </span>
                </div>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Condition:</strong> Items must be returned in their original, unused condition with all tags attached.</li>
                    <li style={styles.listItem}><strong>Approval:</strong> All returns are subject to approval by our customer support team upon contact. We reserve the right to deny returns for items that show signs of wear, damage, or alteration.</li>
                    <li style={styles.listItem}><strong>Defective Items:</strong> If you receive a damaged or defective item, please report it to the number above within 48 hours of delivery.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>3. Shipping & Delivery</h3>
                <p>
                    We are committed to delivering your order promptly and securely.
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Flat Rate:</strong> We apply a flat shipping fee of <strong>$7.00</strong> per order.</li>
                    <li style={styles.listItem}><strong>Coverage:</strong> We currently ship exclusively within the United States.</li>
                    <li style={styles.listItem}><strong>Liability:</strong> While we partner with reliable carriers (such as USPS), shipping times are estimates. Kanchans Choice is not liable for delays caused by weather, carrier operational issues, or incorrect addresses provided by the customer.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>4. Product Accuracy & Pricing</h3>
                <p>
                    We strive for complete accuracy, but errors may occur.
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Pricing Errors:</strong> In the event a product is listed at an incorrect price due to typographical error or systems error, Kanchans Choice reserves the right to refuse or cancel any orders placed for the product listed at the incorrect price.</li>
                    <li style={styles.listItem}><strong>Availability:</strong> All products are subject to availability, and we reserve the right to limit the quantity of products we supply or discontinue any product at any time.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>5. Payment Security</h3>
                <p>
                    We take your security seriously.
                </p>
                <p>
                    Payment must be made in full at checkout. We utilize secure, third-party payment gateways (such as Stripe or PayPal) to process transactions. <strong>We do not collect, store, or have access to your full credit or debit card numbers.</strong> All sensitive payment information is encrypted and handled by the payment processor.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>6. User Conduct</h3>
                <p>
                    By using this site, you agree not to:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>Use our products for any illegal or unauthorized purpose.</li>
                    <li style={styles.listItem}>Transmit any worms, viruses, or any code of a destructive nature.</li>
                    <li style={styles.listItem}>Violate any international, federal, or state regulations.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>7. Limitation of Liability</h3>
                <p>
                    To the fullest extent permitted by law, Kanchans Choice shall not be liable for any special, incidental, or consequential damages resulting from the use or inability to use the products or this website. This includes, but is not limited to, damages for loss of profits, data, or other intangibles.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>8. Governing Law</h3>
                <p>
                    These Terms and Conditions and any separate agreements whereby we provide you services shall be governed by and construed in accordance with the laws of the <strong>Commonwealth of Virginia</strong>, without regard to its conflict of law provisions.
                </p>
            </div>

            <hr style={{ margin: "40px 0", borderTop: "1px solid #ccc" }} />

            <div style={styles.section}>
                <h3>Privacy Summary</h3>
                <p>
                    Your privacy is paramount. We collect only the information necessary to process your order (name, shipping address, email). We do not sell your data to third parties. For full details, please refer to our full Privacy Policy page.
                </p>
            </div>


        </div>
    );
};

export default Policy;